import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavigationService } from '../../../core/services/navigation.service';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../../../core/services/theme.service';

interface PageInfo {
  title: string;
  subtitle: string;
  icon?: string;
}

@Component({
  selector: 'app-page-header',
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderComponent {
  private router = inject(Router);
  private navigationSvc = inject(NavigationService);
  public themeService = inject(ThemeService);

  private currentRoute = signal<string>('');

  private pageInfoMap: Record<string, PageInfo> = {
    '/': {
      icon: 'fa-home',
      title: 'Dashboard',
      subtitle: ''
    },
    '/dashboard': {
      icon: 'fa-home',
      title: 'Dashboard',
      subtitle: 'Find your next great investment faster.'
    },
    '/picks': {
      icon: 'fa-star',
      title: 'Yardie Picks',
      subtitle: 'Di Yardie\'s curated collections of high-quality investment opportunities, no rubbish!'
    },
    '/tools': {
      icon: 'fa-wrench',
      title: 'Toolbox',
      subtitle: 'Essential financial tools &amp; calculators for quick decision making'
    },
    '/profile': {
      icon: 'fa-user',
      title: 'Profile',
      subtitle: 'Manage your account settings and preferences'
    },
    '/calculators': {
      icon: 'fa-calculator',
      title: 'Calculators',
      subtitle: 'Powerful financial calculators to help with your investment planning'
    },
    '/payment': {
      icon: 'fa-credit-card',
      title: 'Payment',
      subtitle: 'Manage your subscription and billing information'
    },
    '/pricing': {
      icon: 'fa-dollar',
      title: 'Pricing',
      subtitle: 'Choose the perfect plan for your investment journey'
    },
    '/terms': {
      icon: 'fa-home',
      title: 'Terms of Service',
      subtitle: 'Review our terms and conditions for using Wall St. Yardie'
    },
    '/privacy': {
      title: 'Privacy Policy',
      subtitle: 'Learn how we protect and handle your personal information'
    },
    '/disclaimer': {
      title: 'Disclaimer',
      subtitle: 'Important legal information and risk disclosures'
    },
    '/cookies': {
      title: 'Cookie Policy',
      subtitle: 'Information about how we use cookies and tracking technologies'
    },
    '/refund': {
      title: 'Refund Policy',
      subtitle: 'Our policy on refunds and cancellations'
    },
    '/about': {
      icon: 'fa-home',
      title: 'About Us',
      subtitle: 'Learn more bout Wall St. Yardie'
    },
     '/sectors': {
      icon: 'fa-building',
      title: 'Sectors',
      subtitle: 'S&P 500 sectors'
    },
     '/congress': {
      icon: 'fa-building-columns',
      title: 'Congress Trades',
      subtitle: 'what is Congress buying!'
    },
     '/watchlist': {
      icon: 'fa-bookmark',
      title: 'Watchlist',
      subtitle: 'Track your Stocks'
    }
  };

  currentPageInfo = computed(() => {
    const route = this.currentRoute();
    const activeLink = this.navigationSvc.activeLink();

    // First try exact match
    if (this.pageInfoMap[route]) {
      return this.pageInfoMap[route];
    }

    // If no exact match, try to find parent route match based on active link
    const parentRoute = this.findParentRoute(route, activeLink);
    if (parentRoute) {
      return this.pageInfoMap[parentRoute];
    }

    // Default fallback
    return {
      title: 'Wall St. Yardie',
      subtitle: 'Invest with clarity, confidence, and a little Yardie flavor.'
    };
  });

  private findParentRoute(currentRoute: string, activeLink: string): string | null {
    // Define parent routes that should be inherited by child routes
    const parentRoutes = [
      '/tools',
      '/sectors',
      '/sector',
      '/congress',
      '/watchlist'
    ];

    // Check if current route starts with any parent route
    for (const parentRoute of parentRoutes) {
      if (currentRoute.startsWith(parentRoute + '/') || currentRoute === parentRoute) {
        // Return the parent route if it exists in our map
        if (this.pageInfoMap[parentRoute]) {
          return parentRoute;
        }
      }
    }

    // Fallback: use activeLink to determine parent route
    if (activeLink === 'sectors' && currentRoute.startsWith('/sector/')) {
      return '/sector';
    }
    if (activeLink === 'tools' && currentRoute.startsWith('/tools/')) {
      return '/tools';
    }
    if (activeLink === 'congress' && currentRoute.startsWith('/congress/')) {
      return '/congress';
    }
    if (activeLink === 'watchlist' && currentRoute === '/watchlist') {
      return '/watchlist';
    }

    return null;
  }

  constructor() {
    // Listen to navigation events to update current route
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.urlAfterRedirects);
      });

    // Set initial route
    this.currentRoute.set(this.router.url);
  }
}
